// Version: 1.0 - When to Visit ER Section Component
import React from 'react';
import './WhenToVisit.css';

const WhenToVisit: React.FC = () => {
  const emergencySymptoms = [
    {
      title: 'Worst Headache of Your Life',
      description: 'A sudden, severe headache that is unlike any you\'ve ever experienced, sometimes called a "thunderclap headache."',
      severity: 'critical'
    },
    {
      title: 'Headache with Fever & Stiff Neck',
      description: 'These symptoms together may indicate meningitis, which requires immediate medical attention.',
      severity: 'critical'
    },
    {
      title: 'Headache After Head Injury',
      description: 'Any new or worsening headache after trauma to the head needs immediate evaluation.',
      severity: 'critical'
    },
    {
      title: 'Sudden Vision Changes',
      description: 'Loss of vision, double vision, or visual disturbances accompanying a headache.',
      severity: 'high'
    },
    {
      title: 'Confusion or Difficulty Speaking',
      description: 'Any neurological symptoms including confusion, slurred speech, or trouble understanding.',
      severity: 'critical'
    },
    {
      title: 'Weakness or Numbness',
      description: 'Numbness, weakness, or paralysis, especially on one side of the body.',
      severity: 'critical'
    },
    {
      title: 'Seizures with Headache',
      description: 'Any seizure activity occurring with headache requires immediate emergency care.',
      severity: 'critical'
    },
    {
      title: 'Headache with High Fever',
      description: 'Persistent severe headache accompanied by fever over 103°F (39.4°C).',
      severity: 'high'
    }
  ];

  return (
    <section id="when-to-visit" className="when-to-visit">
      <div className="container">
        <div className="section-header">
          <h2>When to Go to the ER</h2>
          <p>
            Knowing when a headache requires emergency care can save your life. 
            If you experience any of these symptoms, seek immediate medical attention.
          </p>
        </div>

        <div className="emergency-call-box">
          <div className="call-icon">
            <i className="fa fa-phone-alt"></i>
          </div>
          <div className="call-content">
            <h3>If in doubt, call 911</h3>
            <p>
              Don't wait if you're experiencing severe symptoms. Emergency services can 
              provide immediate assistance and transport you safely to the nearest ER.
            </p>
          </div>
          <div className="call-number">
            <span>Emergency</span>
            <strong>911</strong>
          </div>
        </div>

        <div className="symptoms-grid">
          {emergencySymptoms.map((symptom, index) => (
            <div key={index} className={`symptom-card ${symptom.severity}`}>
              <div className="symptom-indicator">
                <i className={symptom.severity === 'critical' ? 'fa fa-exclamation-triangle' : 'fa fa-exclamation-circle'}></i>
              </div>
              <div className="symptom-content">
                <h4>{symptom.title}</h4>
                <p>{symptom.description}</p>
              </div>
              <div className={`severity-badge ${symptom.severity}`}>
                {symptom.severity === 'critical' ? 'Call 911' : 'Seek Care'}
              </div>
            </div>
          ))}
        </div>

        <div className="additional-info">
          <div className="info-card">
            <h4><i className="fa fa-check-circle"></i> What to Tell the ER</h4>
            <ul>
              <li>When the headache started and how suddenly</li>
              <li>Location and quality of the pain</li>
              <li>Any associated symptoms</li>
              <li>Your headache history</li>
              <li>Current medications</li>
              <li>Any recent head trauma or illness</li>
            </ul>
          </div>
          <div className="info-card">
            <h4><i className="fa fa-hospital"></i> What to Expect at the ER</h4>
            <ul>
              <li>Triage assessment of symptoms</li>
              <li>Neurological examination</li>
              <li>Possible imaging (CT/MRI)</li>
              <li>Blood tests if needed</li>
              <li>Pain management treatment</li>
              <li>Follow-up care recommendations</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhenToVisit;
